import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { 
  getMyNotifications, 
  getSentNotifications,
  markNotificationAsRead,
  markNotificationAsSeen,
  deleteNotification,
  updateNotification
} from '../features/notifications/notificationSlice';
import NotificationsList from '../components/notifications/NotificationsList';
import NotificationEditDialog from '../components/notifications/NotificationEditDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Spinner } from '../components/ui/spinner';
import { refreshAppCounts } from '../lib/utils';

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { notifications, isLoading, isError } = useSelector(
    (state) => state.notifications
  );

  const [tabValue, setTabValue] = useState('received');
  const [displayedNotifications, setDisplayedNotifications] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    message: '',
    isImportant: false
  });

  // Calculate unread notifications count
  const unreadCount = Array.isArray(displayedNotifications) 
    ? displayedNotifications.filter(n => !n.isRead && tabValue === 'received').length 
    : 0;

  // Load notifications when component mounts
  useEffect(() => {
    console.log('Notifications component mounted');
    console.log('Initial notifications state:', {
      count: notifications?.length || 0,
      isLoading,
      isError,
      userRole: user?.role
    });

    if (tabValue === 'received' && notifications && notifications.length > 0) {
      console.log('Using existing notifications from store:', notifications.length);
      setDisplayedNotifications(notifications);
    }
  }, []);

  // Load notifications when tab changes
  useEffect(() => {
    console.log(`Loading notifications, tab: ${tabValue === 'received' ? 'Received' : 'Sent'}`);
    if (tabValue === 'received') {
      console.log('Dispatching getMyNotifications for tab: Received');
      dispatch(getMyNotifications())
        .unwrap()
        .then((result) => {
          console.log(`Successfully loaded ${result.length} notifications for the current user`);
        })
        .catch((error) => {
          console.error('Failed to load notifications:', error);
          toast.error(t('notifications.loadFailed'));
        });
    } else {
      console.log('Dispatching getSentNotifications for tab: Sent');
      dispatch(getSentNotifications())
        .unwrap()
        .then((result) => {
          console.log(`Successfully loaded ${result.length} sent notifications`);
        })
        .catch((error) => {
          console.error('Failed to load sent notifications:', error);
          toast.error(t('notifications.loadSentFailed'));
        });
    }
  }, [tabValue, dispatch]);

  // Update displayed notifications when store changes
  useEffect(() => {
    if (notifications && Array.isArray(notifications)) {
      console.log(`Updating displayed notifications: ${notifications.length} total`);
      setDisplayedNotifications(notifications);
    }
  }, [notifications]);

  // Handle tab change
  const handleChangeTab = useCallback((newValue) => {
    console.log(`Switching to tab ${newValue === 'received' ? 'Received' : 'Sent'}`);
    setTabValue(newValue);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (tabValue === 'received') {
      dispatch(getMyNotifications());
    } else {
      dispatch(getSentNotifications());
    }
  }, [tabValue, dispatch]);

  // Handle mark as read
  const handleMarkAsRead = useCallback((notificationId) => {
    console.log(`Marking notification ${notificationId} as read`);
    dispatch(markNotificationAsRead(notificationId))
      .unwrap()
      .then(() => {
        toast.success(t('notifications.markedAsRead'));
        // Dispatch custom event to refresh header counts
        refreshAppCounts();
      })
      .catch((error) => {
        console.error('Failed to mark notification as read:', error);
        toast.error(t('notifications.markAsReadFailed'));
      });
  }, [dispatch]);

  // Handle mark as seen
  const handleMarkAsSeen = useCallback((notificationId) => {
    console.log(`Marking notification ${notificationId} as seen`);
    dispatch(markNotificationAsSeen(notificationId))
      .unwrap()
      .then(() => {
        toast.success(t('notifications.markedAsSeen'));
        // CRITICAL FIX: Removed full refetch to prevent race condition with stale cache
        // dispatch custom event to refresh header counts
        refreshAppCounts();
      })
      .catch((error) => {
        console.error('Failed to mark notification as seen:', error);
        toast.error(t('notifications.markAsSeenFailed'));
      });
  }, [dispatch, tabValue]);

  // Handle edit notification
  const handleEdit = useCallback((event, notification) => {
    event.preventDefault();
    console.log('Editing notification:', notification._id);
    setCurrentNotification(notification);
    setEditForm({
      title: notification.title,
      message: notification.message,
      isImportant: notification.isImportant || false
    });
    setEditDialogOpen(true);
  }, []);

  // Handle delete notification
  const handleDelete = useCallback((notificationId) => {
    if (window.confirm(t('notifications.deleteConfirm'))) {
      console.log(`Deleting notification ${notificationId}`);
      dispatch(deleteNotification(notificationId))
        .unwrap()
        .then(() => {
          toast.success(t('notifications.deleted'));
          handleRefresh();
        })
        .catch((error) => {
          console.error('Failed to delete notification:', error);
          toast.error(t('notifications.deleteFailed'));
        });
    }
  }, [dispatch, handleRefresh]);

  // Handle save edit
  const handleSaveEdit = useCallback(() => {
    if (!currentNotification) return;

    console.log('Saving notification edit:', currentNotification._id);
    dispatch(updateNotification({
      id: currentNotification._id,
      notificationData: editForm
    }))
      .unwrap()
      .then(() => {
        toast.success(t('notifications.updated'));
        setEditDialogOpen(false);
        setCurrentNotification(null);
        handleRefresh();
      })
      .catch((error) => {
        console.error('Failed to update notification:', error);
        toast.error(t('notifications.updateFailed'));
      });
  }, [currentNotification, editForm, dispatch, handleRefresh]);

  // Handle form change
  const handleFormChange = useCallback((field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle navigate to notification detail
  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
      {/* Page Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-wide text-foreground mb-2">
            {t('notifications.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('notifications.subtitle') || 'Manage your notifications and communications'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            handleRefresh();
            toast.info(t('notifications.refreshing'));
          }}
          className="w-full sm:w-auto transition-[transform,box-shadow] hover:scale-105 hover:shadow-md hover:border-primary hover:bg-primary/5 hover:text-primary"
        >
          <RefreshCw className="mr-2 w-4 h-4" />
          {t('notifications.refresh')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <Tabs value={tabValue} onValueChange={handleChangeTab} className="w-full">
            <TabsList className={`grid w-full ${(user?.role === 'teacher' || user?.role === 'admin') ? 'grid-cols-2' : 'grid-cols-1'} gap-1 bg-muted`}>
              <TabsTrigger value="received" className="relative flex items-center justify-center text-sm">
                <span className="truncate">{t('notifications.received')}</span>
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="ml-1 sm:ml-2 h-4 w-4 sm:h-5 sm:w-5 rounded-full p-0 text-xs flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              {(user?.role === 'teacher' || user?.role === 'admin') && (
                <TabsTrigger value="sent" className="text-sm">{t('notifications.sent')}</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="received" className="mt-4">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Spinner className="text-primary" />
                </div>
              ) : (
                <NotificationsList
                  notifications={displayedNotifications}
                  tabValue="received"
                  user={user}
                  onMarkAsRead={handleMarkAsRead}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onNavigate={handleNavigate}
                />
              )}
            </TabsContent>
            
            {(user?.role === 'teacher' || user?.role === 'admin') && (
              <TabsContent value="sent" className="mt-4">
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Spinner className="text-primary" />
                  </div>
                ) : (
                  <NotificationsList
                    notifications={displayedNotifications}
                    tabValue="sent"
                    user={user}
                    onMarkAsRead={handleMarkAsRead}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onNavigate={handleNavigate}
                  />
                )}
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      <NotificationEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        notification={currentNotification}
        editForm={editForm}
        onFormChange={handleFormChange}
        onSave={handleSaveEdit}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Notifications;
